import React from 'react';
import { formatBytes, formatDuration } from '../../photoUpload/caps';
import { FOOTAGE } from '../../lib/operationCopy';

// The two kinds of batch this card narrates (#588).
//
// It was written for photos and then reused verbatim for the video-ingest half
// of the product, so a user uploading two mp4s was told "Uploading photos · 0
// of 2" and advised to re-pick a photo folder they never selected. The copy is
// keyed here rather than typed at each call site so a third medium cannot
// reintroduce that by omission.
//
// `resume` is the only line that differs beyond the noun, and it differs for a
// real reason: a photo batch is picked as a FOLDER and re-picked whole to
// resume (the dedup pass skips what already landed), while clips are picked as
// individual files.
export const UPLOAD_MEDIA = {
  photos: {
    plural: 'photos',
    resume: 'upload the folder again to resume',
  },
  // "clips" comes from lib/operationCopy — the same word the footage list
  // header, the combine CTA and the video dropzone all render.
  clips: {
    plural: FOOTAGE.plural,
    resume: `add the same ${FOOTAGE.plural} again to resume`,
  },
};

// Sticky progress card shown while a batch is uploading. Matches the
// upload-progress comp — head + progress bar + sub-row of live stats.
//
// `media` picks the wording; it defaults to photos, which is what every caller
// that predates the video half meant.
const MediaUploadProgress = ({ progress, itemStates, onCancel, media = 'photos' }) => {
  if (!progress) return null;

  const copy = UPLOAD_MEDIA[media] || UPLOAD_MEDIA.photos;

  const {
    totalItems,
    doneCount,
    failedCount,
    inFlight,
    bytesTransferred,
    totalBytes,
    bytesPerSecond,
    etaSeconds,
    progressPct,
  } = progress;

  const retryingCount = itemStates?.filter((it) => it.status === 'retrying').length || 0;
  const rateMbps = bytesPerSecond > 0
    ? (bytesPerSecond * 8) / (1000 * 1000)
    : null;

  return (
    <div className="pi-upload-card" data-testid="photo-upload-progress" data-media={media}>
      <div className="pi-upload-head">
        <h4>Uploading {copy.plural} · {doneCount.toLocaleString()} of {totalItems.toLocaleString()}</h4>
        {onCancel && (
          <button type="button" className="btn ghost pi-btn-sm" onClick={onCancel}>
            Cancel
          </button>
        )}
      </div>
      <div className="pi-progress-track">
        <div
          className="pi-progress-fill"
          style={{ width: `${Math.min(100, Math.max(0, progressPct)).toFixed(1)}%` }}
        />
      </div>
      <div className="pi-upload-sub">
        <span><b>{formatBytes(bytesTransferred)}</b> of {formatBytes(totalBytes)}</span>
        {rateMbps != null && <span>{rateMbps.toFixed(1)} Mbps</span>}
        {etaSeconds != null && <span>{formatDuration(etaSeconds)} left</span>}
        <span>
          {inFlight} uploading
          {retryingCount > 0 ? ` · ${retryingCount} retried automatically` : ''}
          {failedCount > 0 ? ` · ${failedCount} failed` : ''}
        </span>
        <span>
          <b>Keep this tab open</b> — if it closes, {copy.resume}.
        </span>
      </div>
    </div>
  );
};

export default MediaUploadProgress;
